import React, { useEffect, useState } from 'react';
import useMeta from '../hooks/useMeta';
import toast from 'react-hot-toast';

const emptyWebhookForm = { url: '', secret: '', enabled: false };

const PageSubscriptions = () => {
  const {
    loading, fetchPageSubscriptions, subscribePage, unsubscribePage, syncPages, verifyPageSubscriptions,
    updatePageWebhook, testPageWebhook,
  } = useMeta();
  const [subscriptions, setSubscriptions] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [editingPageId, setEditingPageId] = useState(null);
  const [webhookForm, setWebhookForm] = useState(emptyWebhookForm);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testLoading, setTestLoading] = useState({});

  const load = async () => {
    const data = await fetchPageSubscriptions();
    setSubscriptions(data);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubscribe = async (pageId) => {
    setActionLoading(prev => ({ ...prev, [pageId]: true }));
    try {
      await subscribePage(pageId);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(prev => ({ ...prev, [pageId]: false }));
    }
  };

  const handleUnsubscribe = async (pageId) => {
    setActionLoading(prev => ({ ...prev, [pageId]: true }));
    try {
      await unsubscribePage(pageId);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(prev => ({ ...prev, [pageId]: false }));
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncPages();
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSyncing(false);
    }
  };

  // Confirms with Meta directly whether each page we think is subscribed still is
  // (same check that runs automatically once a day via cron — see cronService.js).
  const handleVerify = async () => {
    setVerifying(true);
    try {
      const result = await verifyPageSubscriptions();
      await load();
      if (result?.revoked > 0) {
        toast.error(`${result.revoked} página(s) perderam a inscrição no Meta e foram marcadas como não inscritas.`);
      } else {
        toast.success(`Verificado: ${result?.checked || 0} página(s) confirmadas, todas em dia.`);
      }
      if (result?.checkFailed > 0) {
        toast.error(`${result.checkFailed} página(s) não puderam ser verificadas — veja o erro na tabela.`);
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Falha ao verificar inscrições');
    } finally {
      setVerifying(false);
    }
  };

  const openWebhookEditor = (sub) => {
    setEditingPageId(sub.pageId);
    setWebhookForm({
      url: sub.webhookUrl || '',
      secret: sub.webhookSecret || '',
      enabled: Boolean(sub.webhookEnabled),
    });
  };

  const closeWebhookEditor = () => {
    setEditingPageId(null);
    setWebhookForm(emptyWebhookForm);
  };

  const generateSecret = () => {
    const random = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '');
    setWebhookForm(prev => ({ ...prev, secret: random }));
  };

  const handleSaveWebhook = async (pageId) => {
    setSavingWebhook(true);
    try {
      await updatePageWebhook(pageId, {
        webhookUrl: webhookForm.url.trim() || null,
        webhookSecret: webhookForm.secret.trim() || null,
        webhookEnabled: webhookForm.enabled,
      });
      await load();
      closeWebhookEditor();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Falha ao salvar webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleTestWebhook = async (pageId) => {
    setTestLoading(prev => ({ ...prev, [pageId]: true }));
    try {
      const result = await testPageWebhook(pageId);
      if (result?.success) {
        toast.success(`Endpoint respondeu HTTP ${result.status}`);
      } else {
        toast.error(result?.error || 'Endpoint não respondeu corretamente');
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Falha ao testar webhook');
    } finally {
      setTestLoading(prev => ({ ...prev, [pageId]: false }));
    }
  };

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-12 flex justify-between items-end">
        <div>
          <h1 className="font-headline font-extrabold text-4xl tracking-tight text-on-surface mb-2">Inscrição de Páginas</h1>
          <p className="text-secondary font-body">Gerencie quais páginas do Facebook estão inscritas para receber leads via webhook</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleVerify}
            disabled={verifying || loading}
            className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-2"
            title="Confirma direto com o Meta se as páginas marcadas como inscritas continuam recebendo leadgen"
          >
            <span className="material-symbols-outlined text-sm">verified</span>
            {verifying ? 'Verificando...' : 'Verificar Agora'}
          </button>
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">sync</span>
            {syncing ? 'Sincronizando...' : 'Sincronizar Páginas'}
          </button>
        </div>
      </div>

      <div className="bg-surface-container-low rounded-xl p-4 mb-8 border border-amber-200 bg-amber-50 flex items-start gap-3">
        <span className="material-symbols-outlined text-amber-600 mt-0.5 shrink-0">info</span>
        <p className="text-sm text-amber-800">
          Para receber leads via webhook do Meta, cada página precisa estar <strong>inscrita</strong> nesta aplicação.
          Clique em <strong>Inscrever</strong> nas páginas desejadas. Se a lista estiver vazia, clique em <strong>Sincronizar Páginas</strong> primeiro.
          O status exibido vem do nosso banco — clique em <strong>Verificar Agora</strong> pra confirmar direto com o Meta (isso também roda sozinho, uma vez por dia).
        </p>
      </div>

      <div className="bg-surface-container-low rounded-xl overflow-hidden shadow-sm">
        {loading && subscriptions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-slate-500 text-sm">Carregando páginas...</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="p-12 text-center">
            <span className="material-symbols-outlined text-5xl text-slate-300 mb-4 block">pages</span>
            <p className="font-headline font-bold text-xl text-slate-700 mb-2">Nenhuma página encontrada</p>
            <p className="text-slate-500 text-sm mb-6">Clique em "Sincronizar Páginas" para buscar suas páginas do Facebook.</p>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-semibold"
            >
              Sincronizar Agora
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-container-lowest border-b border-outline-variant">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Página</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">ID</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Inscrito em</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Webhook</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {subscriptions.map((sub) => (
                <React.Fragment key={sub.pageId}>
                <tr className="bg-white hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-on-surface">{sub.pageName}</td>
                  <td className="px-6 py-4 font-mono text-secondary text-xs">{sub.pageId}</td>
                  <td className="px-6 py-4">
                    {sub.isSubscribed ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Inscrita
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                        Não inscrita
                      </span>
                    )}
                    {sub.lastError && (
                      <p className="text-xs text-red-500 mt-1">{sub.lastError}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-500 text-xs">
                    {sub.subscribedAt ? new Date(sub.subscribedAt).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-6 py-4">
                    {sub.webhookEnabled && sub.webhookUrl ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                        Configurado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                        Não configurado
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => editingPageId === sub.pageId ? closeWebhookEditor() : openWebhookEditor(sub)}
                        className="px-4 py-1.5 rounded-full border border-outline-variant text-on-surface text-xs font-semibold hover:bg-white transition-all"
                      >
                        {editingPageId === sub.pageId ? 'Fechar' : 'Webhook'}
                      </button>
                      {actionLoading[sub.pageId] ? (
                        <div className="inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      ) : sub.isSubscribed ? (
                        <button
                          onClick={() => handleUnsubscribe(sub.pageId)}
                          className="px-4 py-1.5 rounded-full border border-red-300 text-red-600 text-xs font-semibold hover:bg-red-50 transition-all"
                        >
                          Desinscrever
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSubscribe(sub.pageId)}
                          className="px-4 py-1.5 rounded-full primary-pill-gradient text-white text-xs font-semibold hover:opacity-90 transition-all"
                        >
                          Inscrever
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {editingPageId === sub.pageId && (
                  <tr className="bg-slate-50">
                    <td colSpan={6} className="px-6 py-5">
                      <div className="max-w-2xl">
                        <p className="text-xs text-secondary mb-3">
                          Configure um endpoint externo que recebe um <strong>POST</strong> com os dados do lead assim que ele for processado (dispara sozinho, sem reenvio automático em caso de falha).
                        </p>
                        <div className="grid gap-3">
                          <label className="text-xs font-semibold text-on-surface">
                            URL do endpoint
                            <input
                              type="url"
                              placeholder="https://exemplo.com/webhook/leads"
                              value={webhookForm.url}
                              onChange={(e) => setWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                              className="mt-1 w-full px-3 py-2 rounded-lg border border-outline-variant text-sm font-normal"
                            />
                          </label>
                          <label className="text-xs font-semibold text-on-surface">
                            Secret (assina o payload em <span className="font-mono">X-Webhook-Signature</span>, opcional)
                            <div className="mt-1 flex gap-2">
                              <input
                                type="text"
                                placeholder="Deixe em branco para não assinar"
                                value={webhookForm.secret}
                                onChange={(e) => setWebhookForm(prev => ({ ...prev, secret: e.target.value }))}
                                className="flex-1 px-3 py-2 rounded-lg border border-outline-variant text-sm font-normal font-mono"
                              />
                              <button
                                type="button"
                                onClick={generateSecret}
                                className="px-3 py-2 rounded-lg border border-outline-variant text-xs font-semibold hover:bg-white transition-all shrink-0"
                              >
                                Gerar
                              </button>
                            </div>
                          </label>
                          <label className="flex items-center gap-2 text-xs font-semibold text-on-surface">
                            <input
                              type="checkbox"
                              checked={webhookForm.enabled}
                              onChange={(e) => setWebhookForm(prev => ({ ...prev, enabled: e.target.checked }))}
                            />
                            Ativo (disparar POST a cada novo lead desta página)
                          </label>
                        </div>
                        <div className="flex items-center gap-3 mt-4">
                          <button
                            onClick={() => handleSaveWebhook(sub.pageId)}
                            disabled={savingWebhook}
                            className="px-5 py-1.5 rounded-full primary-pill-gradient text-white text-xs font-semibold hover:opacity-90 transition-all disabled:opacity-50"
                          >
                            {savingWebhook ? 'Salvando...' : 'Salvar'}
                          </button>
                          <button
                            onClick={() => handleTestWebhook(sub.pageId)}
                            disabled={testLoading[sub.pageId] || !sub.webhookUrl}
                            className="px-5 py-1.5 rounded-full border border-outline-variant text-on-surface text-xs font-semibold hover:bg-white transition-all disabled:opacity-50"
                            title={!sub.webhookUrl ? 'Salve uma URL antes de testar' : 'Envia um lead de exemplo para a URL salva (salve antes se alterou)'}
                          >
                            {testLoading[sub.pageId] ? 'Testando...' : 'Testar'}
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PageSubscriptions;
